import colors from 'shared/colors';
import { chartGroupByOption, gridPageChartType } from 'shared/constants.json';
import { computeLabelStep, dataLabelStyle, escapeHtml, formatNoData, formatTooltipCard, formatTooltipRow, isDateGrouping, nudgeFirstLabel } from '@/composables/grid-page/chart/utils';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

export default function useLineChart(result, chart) {
  const { t } = useI18n();
  const isStacked = computed(() => [gridPageChartType.line, gridPageChartType.bar, gridPageChartType.column].includes(chart.value.chartType)
      && chart.value.aggregationConfig.stackOptions.groupBy !== chartGroupByOption.none);

  const chartType = computed(() => {
    if (isStacked.value) {
      return 'area';
    }
    return 'line';
  });

  const series = computed(() => {
    if (chart.value.chartType !== gridPageChartType.line) {
      return [];
    }

    if (result.value === null) {
      return [];
    }

    return result.value.series;
  });

  const categories = computed(() => {
    if (result.value === null) {
      return [];
    }

    return result.value.categories;
  });

  const chartOptions = computed(() => ({
    chart: {
      animation: false,
      type: chartType.value,
      events: {
        render() {
          nudgeFirstLabel(this);
        },
      },
    },
    plotOptions: {
      [chartType.value]: {
        stacking: 'normal',
        fillOpacity: 0.15,
        dataLabels: {
          enabled: !isStacked.value,
          y: -6,
          style: dataLabelStyle,
        },
      },
      series: {
        events: {
          hide(e) {
            const chart = this.chart;
            const visibleCount = chart.series.filter((s) => s.visible).length;

            if (visibleCount === 0 && !this.visible) {
              chart.series[e.target.index].show();
            }
          },
        },
      },
    },
    yAxis: {
      title: { text: null },
      labels: { enabled: true },
      gridLineWidth: 1,
      stackLabels: {
        enabled: isStacked.value,
        style: dataLabelStyle,
      },
    },
    legend: {
      enabled: isStacked.value,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      y: 10,
      events: {
        itemClick(e) {
          const chart = this.chart;
          const visibleCount = chart.series.filter((s) => s.visible).length;

          if (visibleCount === 1 && e.legendItem.visible) {
            return false;
          }
          return true;
        },
      },
    },
    xAxis: {
      categories: categories.value,
      lineWidth: 1,
      ...(isDateGrouping(chart.value.groupBy?.groupBy) && { tickInterval: computeLabelStep(categories.value.length) }),
      labels: {
        enabled: true,
        ...(isDateGrouping(chart.value.groupBy?.groupBy)
          ? { autoRotation: false }
          : { autoRotation: [-45], style: { textOverflow: 'ellipsis', width: '80px' } }),
      },
      crosshair: { color: colors.grey.lighten6, width: 1, zIndex: 0 },
    },
    series: series.value,
    tooltip: {
      shared: true,
      formatter() {
        const points = this.points;
        const noData = t('highcharts.noDataShort');
        if (points.length > 1) {
          const header = `<div style="margin-bottom:6px;color:${colors.grey.darken4}">${escapeHtml(points[0].key)}</div>`;
          const rows = points.filter((p) => p.y !== 0).map((p) => formatTooltipRow(p.color, p.series.name, p.y)).join('');
          return formatTooltipCard(header + (rows || formatNoData(noData)));
        }
        const p = points[0];
        if (p.y === 0) {
          const header = `<div style="margin-bottom:6px;color:${colors.grey.darken4}">${escapeHtml(p.key || p.point.name)}</div>`;
          return formatTooltipCard(header + formatNoData(noData));
        }
        return formatTooltipCard(formatTooltipRow(p.color, p.key || p.point.name, p.y));
      },
    },
  }));

  return { chartOptions };
}
